import { Dispatch } from 'redux'

import { useThunkActions } from 'redux/actions/useThunkActions'

import {
  create,
  destroy,
  getAll,
  getData,
  TPatchKeyMetricBody,
  TPostKeyMetricBody,
  update,
  updateOrder
} from 'redux/api/KeyMetrics'
import * as PinnedKeyMetricsApi from 'redux/api/PinnedKeyMetrics'

import * as groupActions from 'redux/actions/Groups/actions'

import { toggleSnackbarOpen } from 'redux/actions/Snackbar'
import { SnackbarMessage } from 'types/GlobalSnackbar'
import { getKeyMetricDataKey, getMetricDataKey } from 'utils/metric'
import {
  TKeyMetricDataBody,
  UpdateKeyMetricOrderItem
} from 'types/GlobalKeyMetric'
import { selectGlobalFilterForApi } from 'redux/selectors/GlobalFilter'
import { ApplicationState } from 'redux/Stores/types'

import * as actions from './actions'

export function tryGetAllKeyMetrics(dispatch: Dispatch) {
  dispatch(actions.getAll())

  getAll()
    .then((response) => {
      dispatch(actions.getAllSuccess(response.data.data))
    })
    .catch(() => {
      dispatch(actions.getAllFailed())
    })
}

function tryCreateKeyMetric(
  body: TPostKeyMetricBody,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.create())

    create(body)
      .then((response) => {
        dispatch(actions.createSuccess(response.data.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.KpiCreated))

        callback?.()
      })
      .catch(() => {
        dispatch(actions.createFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.KpiCreatedFailed))

        callback?.(true)
      })
  }
}

function tryDeleteKeyMetric(
  id: string,
  parentId: string | null,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.destroy())

    destroy(id)
      .then(() => {
        dispatch(actions.destroySuccess({ id, parentId }))

        dispatch(toggleSnackbarOpen(SnackbarMessage.KpiDeleted))

        callback?.()
      })
      .catch(() => {
        dispatch(actions.destroyFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.KpiDeletedFailed))

        callback?.(true)
      })
  }
}

function tryUpdateKeyMetric(id: string, body: TPatchKeyMetricBody) {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    dispatch(actions.update())

    const prevMetric = getState().KeyMetricStore.data[id]?.metric

    update(id, body)
      .then((response) => {
        const updatedKeyMetric = response.data.data

        dispatch(actions.updateSuccess(updatedKeyMetric))

        const nextMetric = updatedKeyMetric.metric
        const calcChanged =
          !prevMetric ||
          prevMetric.id !== nextMetric.id ||
          getMetricDataKey(prevMetric.settings) !==
            getMetricDataKey(nextMetric.settings)

        if (calcChanged) {
          dispatch(actions.clearRawData(id))
        }

        dispatch(toggleSnackbarOpen(SnackbarMessage.KpiUpdated))
      })
      .catch(() => {
        dispatch(actions.updateFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.KpiUpdatedFailed))
      })
  }
}

function tryUpdateOrderKeyMetric(body: UpdateKeyMetricOrderItem[]) {
  return (dispatch: Dispatch) => {
    dispatch(actions.updateOrder())

    updateOrder(body)
      .then((response) => {
        dispatch(actions.updateOrderSuccess(response.data.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.KpiOrderUpdated))
      })
      .catch(() => {
        dispatch(actions.updateOrderFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.KpiOrderUpdatedFailed))
      })
  }
}

function tryPinKeyMetric(keyMetricId: string, comment: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.pin())

    PinnedKeyMetricsApi.create({
      comment,
      key_metric_id: keyMetricId
    })
      .then(() => {
        getAll()
          .then((response) => {
            dispatch(actions.pinSuccess(response.data.data))
          })
          .catch(() => {
            dispatch(actions.pinFailed())
          })
      })
      .catch(() => {
        dispatch(actions.pinFailed())
      })
  }
}

function tryUpdatePinComment(pinnedKeyMetricId: string, comment: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.pin())

    PinnedKeyMetricsApi.update(pinnedKeyMetricId, { comment })
      .then(() => {
        getAll()
          .then((response) => {
            dispatch(actions.pinSuccess(response.data.data))
          })
          .catch(() => {
            dispatch(actions.pinFailed())
          })
      })
      .catch(() => {
        dispatch(actions.pinFailed())
      })
  }
}

function tryUnpinKeyMetric(pinnedKeyMetricId: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.pin())

    PinnedKeyMetricsApi.destroy(pinnedKeyMetricId)
      .then(() => {
        getAll()
          .then((response) => {
            dispatch(actions.pinSuccess(response.data.data))
          })
          .catch(() => {
            dispatch(actions.pinFailed())
          })
      })
      .catch(() => {
        dispatch(actions.pinFailed())
      })
  }
}

function tryPinKeyMetricOnGroup(
  keyMetricId: string,
  groupId: string,
  comment: string
) {
  return (dispatch: Dispatch) => {
    PinnedKeyMetricsApi.create({
      comment,
      group_id: groupId,
      key_metric_id: keyMetricId
    })
      .then((response) => {
        const { id, comment: returnedComment } = response.data.data

        dispatch(
          groupActions.pinKeyMetricOnGroupSuccess({
            groupId,
            pin: { comment: returnedComment, id, key_metric_id: keyMetricId }
          })
        )
      })
      .catch(() => {
        dispatch(actions.pinFailed())
      })
  }
}

function tryUnpinKeyMetricOnGroup(pinnedKeyMetricId: string, groupId: string) {
  return (dispatch: Dispatch) => {
    PinnedKeyMetricsApi.destroy(pinnedKeyMetricId)
      .then(() => {
        dispatch(
          groupActions.unpinKeyMetricOnGroupSuccess({
            groupId,
            pinId: pinnedKeyMetricId
          })
        )
      })
      .catch(() => {
        dispatch(actions.pinFailed())
      })
  }
}

function tryUpdateGroupPinComment(
  pinnedKeyMetricId: string,
  groupId: string,
  comment: string
) {
  return (dispatch: Dispatch) => {
    PinnedKeyMetricsApi.update(pinnedKeyMetricId, { comment })
      .then(() => {
        dispatch(
          groupActions.updateGroupPinCommentSuccess({
            comment,
            groupId,
            pinId: pinnedKeyMetricId
          })
        )
      })
      .catch(() => {
        dispatch(actions.pinFailed())
      })
  }
}

function tryGetKeyMetricData(id: string, body?: TKeyMetricDataBody) {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    const state = getState()

    // Skip if an identical request is already in flight (e.g. several
    // components asking for the same key metric before the first response
    // lands).
    if (state.KeyMetricStore.fetching[id]?.[getKeyMetricDataKey(body)]) {
      return
    }

    dispatch(actions.getOneRaw({ id, body }))

    const globalFilter = selectGlobalFilterForApi(state)

    getData(id, globalFilter, body)
      .then((response) =>
        dispatch(
          actions.getOneRawSuccess({ body, data: response.data.data, id })
        )
      )
      .catch((e) =>
        dispatch(
          actions.getOneRawFailed({
            id,
            body,
            status: e?.response?.status ?? null
          })
        )
      )
  }
}

export const useKeyMetricActions = () => {
  return {
    tryCreateKeyMetric: useThunkActions(tryCreateKeyMetric),
    tryDeleteKeyMetric: useThunkActions(tryDeleteKeyMetric),
    tryGetKeyMetricData: useThunkActions(tryGetKeyMetricData),
    tryPinKeyMetric: useThunkActions(tryPinKeyMetric),
    tryPinKeyMetricOnGroup: useThunkActions(tryPinKeyMetricOnGroup),
    tryUnpinKeyMetric: useThunkActions(tryUnpinKeyMetric),
    tryUnpinKeyMetricOnGroup: useThunkActions(tryUnpinKeyMetricOnGroup),
    tryUpdateGroupPinComment: useThunkActions(tryUpdateGroupPinComment),
    tryUpdatePinComment: useThunkActions(tryUpdatePinComment),
    tryUpdateKeyMetric: useThunkActions(tryUpdateKeyMetric),
    tryUpdateOrderKeyMetric: useThunkActions(tryUpdateOrderKeyMetric)
  }
}
